import { computed, inject } from '@angular/core';
import { signalStore, patchState, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TenantSettingsQueryService } from 'app/core/tenant-settings/api/queries/tenant-settings-query.service';
import { hasError, isExecuting, withClearState, withOpTracking } from 'app/shared/store';
import { TenantSettings } from 'app/shared/types/tenant-settings.types';
import { exhaustMap, firstValueFrom } from 'rxjs';

enum TenantSettingsOpType {
    LoadTenantSettings = 'loadTenantSettings',
    ReloadTenantSettings = 'reloadTenantSettings',
}

interface TenantSettingsState {
    tenantSettings: TenantSettings | null;
}

const initialState: TenantSettingsState = {
    tenantSettings: null,
};

export const TenantSettingsStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withClearState(initialState),
    ...withOpTracking(TenantSettingsOpType),
    withComputed((state) => ({
        isLoadingTenantSettings: isExecuting(TenantSettingsOpType.LoadTenantSettings)(state),
        isReloadingTenantSettings: isExecuting(TenantSettingsOpType.ReloadTenantSettings)(state),
        hasLoadTenantSettingsError: hasError(TenantSettingsOpType.LoadTenantSettings)(state),
        hasReloadTenantSettingsError: hasError(TenantSettingsOpType.ReloadTenantSettings)(state),
        isLoaded: computed(() => state.tenantSettings() !== null),
        version: computed(() => state.tenantSettings()?.version ?? null),
        features: computed(() => state.tenantSettings()?.features ?? null),
        defaults: computed(() => state.tenantSettings()?.defaults ?? null),
        calendar: computed(() => state.tenantSettings()?.calendar ?? null),
        behaviour: computed(() => state.tenantSettings()?.behaviour ?? null),
        invoice: computed(() => state.tenantSettings()?.invoice ?? null),
        scheduleDelegateStatusUpdateAllDays: computed(() =>
            state.tenantSettings()?.behaviour.scheduleDelegateStatusUpdateAllDays ?? true),
        trainingHoursLabel: computed(() => state.tenantSettings()?.labels.trainingHours ?? null),
        accountsExportTypeName: computed(() => state.tenantSettings()?.labels.accountsExportTypeName || null),
        apiEnabled: computed(() => state.tenantSettings()?.features.api ?? false),
        dealEnabled: computed(() => state.tenantSettings()?.features.deal ?? false),
        onlineBookingEnabled: computed(() => state.tenantSettings()?.features.onlineBooking ?? false),
        automationEnabled: computed(() => state.tenantSettings()?.features.automation ?? false),
        pricingSchemeEnabled: computed(() => state.tenantSettings()?.features.pricingScheme ?? false),
        dataImportEnabled: computed(() => state.tenantSettings()?.features.dataImport ?? false),
        smsEnabled: computed(() => state.tenantSettings()?.features.sms ?? false),
        multipleCurrencyEnabled: computed(() => state.tenantSettings()?.features.multipleCurrency ?? false),
        eformEnabled: computed(() => state.tenantSettings()?.features.eform ?? false),
        competencyEnabled: computed(() => state.tenantSettings()?.features.competency ?? false),
        xeroIntegrationEnabled: computed(() => state.tenantSettings()?.features.xeroIntegration ?? false),
        trainingHoursEnabled: computed(() => state.tenantSettings()?.features.trainingHours ?? false),
    })),
    withMethods((store, queryService = inject(TenantSettingsQueryService)) => ({
        // Promise (not rxMethod): auth awaits this before routing; track already drives isLoading.
        async loadTenantSettings(): Promise<void> {
            if (store.tenantSettings() !== null) {
                return;
            }
            await firstValueFrom(
                store.track(
                    queryService.getTenantSettings(),
                    TenantSettingsOpType.LoadTenantSettings,
                    (tenantSettings: TenantSettings) => patchState(store, { tenantSettings })
                ),
                { defaultValue: null }
            );
        },
        reloadTenantSettings: rxMethod<void>(
            exhaustMap(() => store.track(
                queryService.getTenantSettings(),
                TenantSettingsOpType.ReloadTenantSettings,
                (tenantSettings: TenantSettings) => patchState(store, { tenantSettings })
            ))
        ),
    }))
);
